import React, { useRef, useState } from "react";
import { Upload } from "lucide-react";

interface PanelFileUploadProps {
  onFileChange?: (file: File | null) => void;
  label?: string;
  accept?: string;
}

const PanelFileUpload: React.FC<PanelFileUploadProps> = ({
  onFileChange,
  label = "Upload File",
  accept = ".csv",
}) => {
  const inputRef = useRef<HTMLInputElement | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [isParsing, setIsParsing] = useState(false);

  const validateCSV = (file: File): boolean => {
    const name = file.name.toLowerCase();
    const isCsv = name.endsWith('.csv') || file.type === 'text/csv' || file.type === 'application/vnd.ms-excel';
    if (!isCsv) {
      setError('Please select a valid CSV file.');
      return false;
    }
    setError(null);
    return true;
  };

  const handleFileSelect = async(file: File | null) => {
    setIsParsing(true);
    if (!file) {
      setSelectedFile(null);
      setError(null);
      if (onFileChange) {
        onFileChange(null);
      }
      return;
    }

    if (!validateCSV(file)) {
      setSelectedFile(null);
      return;
    }

    setSelectedFile(file);
    if (onFileChange) {
      onFileChange(file);
    }
    setIsParsing(false);
  };

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files && e.target.files[0] ? e.target.files[0] : null;
    handleFileSelect(file);
  };

  const onDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    const file = e.dataTransfer.files?.[0] ?? null;
    handleFileSelect(file);
  };

  const onDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleClear = () => {
    setSelectedFile(null);
    setError(null);
    if (inputRef.current) {
      inputRef.current.value = "";
    }
    if (onFileChange) {
      onFileChange(null);
    }
  };

  return (
    <div className="flex flex-col w-full gap-4 h-full justify-items-center">
      {label && (
        <label className="font-semibold text-sm text-gray-700">
          {label}
        </label>
      )}
      <div
        className={`relative w-full rounded-lg border-2 border-dashed transition-colors cursor-pointer ${
          isDragging ? "border-blue-500 bg-blue-50" : "border-gray-300 hover:border-gray-400"
        }`}
        onDrop={onDrop}
        onDragOver={onDragOver}
        onDragLeave={onDragLeave}
      >
        <input
          ref={inputRef}
          type="file"
          accept={accept}
          onChange={handleChange}
          className="absolute inset-0 h-full w-full cursor-pointer opacity-0 z-10"
          aria-label={label}
        />
        <div className="pointer-events-none flex flex-col items-center justify-center py-8 px-4 text-center">
          <Upload className="mb-3 h-8 w-8 text-gray-400" />
          <p className="text-sm text-gray-700">
            <strong className="text-gray-900">
              Click to upload
            </strong>
            {" "}
            or drag and drop CSV file
          </p>
          {selectedFile && !isParsing && (
            <p className="mt-2 text-xs text-gray-500 truncate max-w-full">
              {selectedFile.name}
            </p>
          )}
          {isParsing && (
            <p className="mt-2 text-xs text-gray-500">
              Parsing CSV...
            </p>
          )}
        </div>
      </div>

      {error && (
        <div className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700">
          {error}
        </div>
      )}

      {selectedFile && !isParsing && (
        <div className="flex items-center justify-between rounded-md bg-gray-50 p-2">
          <div className="min-w-0 flex-1">
            <p className="truncate text-sm font-medium text-gray-700">
              {selectedFile.name}
            </p>
            <p className="text-xs text-gray-500">
              {(selectedFile.size / 1024).toFixed(1)}
              {' '}
              KB
            </p>
          </div>
          <button
            type="button"
            onClick={(e) => {
              e.stopPropagation();
              handleClear();
            }}
            className="ml-2 rounded px-2 py-1 text-xs text-gray-600 hover:bg-gray-200 transition-colors"
          >
            Clear
          </button>
        </div>
      )}
      <div className="text-sm text-gray-500">
        To create a panel, start by uploading a CSV file.  This will create a new panel with the given columns.  You can then edit the panel to remove columns and give detailed descriptions of the columns.
        <ul className="list-disc list-inside">
          <li>
            The first row of the CSV file will be used as the headers of the panel.
          </li>
          <li>
            The remaining rows of the CSV file will be used as the data for the panelists.
          </li>
        </ul>
      </div>
    </div>
  );
};

export default PanelFileUpload;
